import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Mail, Phone } from 'lucide-react';
import { siteConfig } from '../data/content';

const PrivacyPolicyPage = () => {
  const lastUpdated = "December 30, 2024";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-teal-50 to-slate-50">
        <div className="container">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 mb-6"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <Shield size={24} className="text-teal-700" />
            </div>
            <div>
              <h1 className="heading-1" data-testid="privacy-policy-title">Privacy Policy</h1>
              <p className="text-sm text-slate-500">Last updated: {lastUpdated}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-8">
              
              {/* Introduction */}
              <div>
                <h2 className="heading-3 mb-4">Introduction</h2>
                <p className="body-medium mb-4">
                  {siteConfig.name} ("we," "us," or "our") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                  information when you visit our website or use our services.
                </p>
                <p className="body-medium">
                  Please read this privacy policy carefully. By using our website or services, 
                  you consent to the practices described in this policy.
                </p>
              </div>

              {/* Information We Collect */}
              <div>
                <h2 className="heading-3 mb-4">Information We Collect</h2>
                <p className="body-medium mb-4">We may collect information that you provide directly to us, including:</p>
                <ul className="list-disc pl-6 space-y-2 body-medium">
                  <li>Contact information (name, email address, phone number)</li>
                  <li>Appointment scheduling preferences</li>
                  <li>General service inquiries</li>
                  <li>Referral source information</li>
                  <li>Insurance plan type (for scheduling purposes only)</li>
                </ul>
                
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Important:</strong> Our website contact forms are designed for general 
                    inquiries only. Please do not submit sensitive medical information, detailed 
                    health history, diagnoses, or other protected health information (PHI) through 
                    these forms. Clinical information should only be shared through approved 
                    healthcare channels.
                  </p>
                </div>
              </div>

              {/* How We Use Your Information */}
              <div>
                <h2 className="heading-3 mb-4">How We Use Your Information</h2>
                <p className="body-medium mb-4">We use the information we collect to:</p>
                <ul className="list-disc pl-6 space-y-2 body-medium">
                  <li>Respond to your inquiries and requests</li>
                  <li>Schedule and confirm appointments</li>
                  <li>Verify insurance eligibility (when applicable)</li>
                  <li>Provide customer support</li>
                  <li>Send appointment reminders</li>
                  <li>Improve our website and services</li>
                </ul>
              </div>

              {/* Information Sharing */}
              <div>
                <h2 className="heading-3 mb-4">Information Sharing</h2>
                <p className="body-medium mb-4">
                  We do not sell, trade, or rent your personal information to third parties. 
                  We may share your information in the following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2 body-medium">
                  <li>With your consent</li>
                  <li>To comply with legal obligations</li>
                  <li>To protect our rights and safety</li>
                  <li>With service providers who assist in our operations (bound by confidentiality agreements)</li>
                </ul>
              </div>

              {/* Data Security */}
              <div>
                <h2 className="heading-3 mb-4">Data Security</h2>
                <p className="body-medium">
                  We implement appropriate technical and organizational measures to protect your 
                  personal information against unauthorized access, alteration, disclosure, or 
                  destruction. However, no method of transmission over the internet is 100% secure, 
                  and we cannot guarantee absolute security.
                </p>
              </div>

              {/* Your Rights */}
              <div>
                <h2 className="heading-3 mb-4">Your Rights</h2>
                <p className="body-medium mb-4">You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2 body-medium">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your information (subject to legal requirements)</li>
                  <li>Opt out of marketing communications</li>
                </ul>
              </div>

              {/* Texas Residents */}
              <div>
                <h2 className="heading-3 mb-4">Texas Residents</h2>
                <p className="body-medium">
                  If you are a Texas resident, you may have additional rights under Texas law. 
                  For questions about your rights or to make a request, please contact us using 
                  the information provided below.
                </p>
              </div>

              {/* Children's Privacy */}
              <div>
                <h2 className="heading-3 mb-4">Children's Privacy</h2>
                <p className="body-medium">
                  Our website is not intended for children under 18 years of age. We do not 
                  knowingly collect personal information from children under 18.
                </p>
              </div>

              {/* Changes to This Policy */}
              <div>
                <h2 className="heading-3 mb-4">Changes to This Policy</h2>
                <p className="body-medium">
                  We may update this Privacy Policy from time to time. The updated version will 
                  be indicated by an updated "Last Updated" date. We encourage you to review this 
                  policy periodically.
                </p>
              </div>

              {/* Contact Information */}
              <div className="pt-6 border-t border-slate-200">
                <h2 className="heading-3 mb-4">Contact Us</h2>
                <p className="body-medium mb-4">
                  If you have questions about this Privacy Policy or our privacy practices, 
                  please contact us:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-teal-600" />
                    <a href={`tel:${siteConfig.phone}`} className="link">{siteConfig.phone}</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-teal-600" />
                    <a href={`mailto:${siteConfig.email}`} className="link">{siteConfig.email}</a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
